import { createMultiStyleConfigHelpers } from "@chakra-ui/react";
import getThemes, { ThemesKeys } from "../shared/themeShared";

const helpers = createMultiStyleConfigHelpers([
  "field",
  "dropdownIndicator",
  "indicatorSeparator",
  "control",
  "placeholder",
  "input",
  "multiValue",
  "multiValueWithSideContent",
  "multiValueRemove",
  "option",
  "valueContainer",
  "menu",
  "indicatorsContainer",
  "clearIndicator",
]);

const baseStyle = (theme: ThemesKeys) => ({
  field: {
    borderRadius: getThemes(theme).radii.lg,
  },
  dropdownIndicator: {
    padding: 0,
    color: getThemes(theme).colors.palette.primary[0],
    icon: getThemes(theme).icons.IcImage,
    _hover: {
      color: getThemes(theme).colors.palette.primary[0],
    },
  },
  indicatorSeparator: {
    display: "none",
  },
  colors: {
    primary: getThemes(theme).colors.palette.primary[95],
    primary25: getThemes(theme).colors.palette.primary[95],
    primary50: getThemes(theme).colors.palette.primary[80],
  },
  control: {
    border: 0,
    boxShadow: `0 0 0 1px ${getThemes(theme).colors.palette.stroke.primary[80]}`,
    boxShadowFocused: `0 0 0 2px ${
      getThemes(theme).colors.palette.primary[50]
    }`,
    alignItems: "center",
    _disabled: {
      backgroundColor: "white",
      boxShadow: `0 0 0 1px ${getThemes(theme).colors.palette.stroke.secondary[90]}`,
    },
    _hover: {
      boxShadow: `0 0 0 1px ${getThemes(theme).colors.palette.stroke.primary[80]}`,
    },
  },
  placeholder: {
    paddingLeft: getThemes(theme).space[1],
    color: getThemes(theme).colors.palette.text.secondary[80],
  },
  input: {
    margin: 0,
    paddingLeft: getThemes(theme).space[1],
  },
  multiValue: {
    backgroundColor: getThemes(theme).colors.palette.surface.primary[95],
    borderRadius: getThemes(theme).radii.lg,
    border: `1px solid ${getThemes(theme).colors.palette.stroke.primary[80]}`,
    alignItems: "center",
    paddingLeft: getThemes(theme).space[2],
    paddingRight: 0,
  },
  multiValueWithSideContent: {
    paddingLeft: "0px",
  },
  multiValueRemove: {
    borderRadius: getThemes(theme).radii.full,
    color: getThemes(theme).colors.palette.primary[50],
    width: "14px",
    height: "14px",
    padding: "0",
    marginLeft: getThemes(theme).space[1],
    marginRight: getThemes(theme).space[2.5],
    border: `1px solid ${getThemes(theme).colors.palette.stroke.primary[50]}`,
    _hover: {
      backgroundColor: getThemes(theme).colors.palette.primary[80],
    },
  },
  option: {
    py: 2,
    px: 4,
    _hover: {
      bg: getThemes(theme).colors.palette.primary[90],
      color: getThemes(theme).colors.palette.primary[0],
    },
    selected: {
      bg: getThemes(theme).colors.palette.primary[95],
      color: getThemes(theme).colors.palette.primary[0],
      _hover: {
        bg: getThemes(theme).colors.palette.primary[90],
        color: getThemes(theme).colors.palette.primary[0],
      },
    },
  },
  valueContainer: {
    padding: 0,
    paddingLeft: getThemes(theme).space[2],
    paddingTop: getThemes(theme).space[0.5],
    paddingBottom: getThemes(theme).space[0.5],
  },
  menu: {
    overflow: "hidden",
    marginTop: 2,
    padding: 0,
  },
  indicatorsContainer: {
    padding: 0,
    justifyContent: "flex-end",
  },
  clearIndicator: {
    cursor: "pointer",
    padding: "4px",
  },
});

const sm = (theme: ThemesKeys) => ({
  field: {
    borderRadius: getThemes(theme).radii.md,
  },
  menu: {
    borderRadius: getThemes(theme).space[2],
  },
  control: {
    minHeight: "32px",
  },
  indicatorsContainer: {
    marginRight: 5,
  },
  multiValue: {
    marginTop: 0,
    marginBottom: 0,
  },
  multiValueLabel: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  option: {
    py: 1,
    px: 2.5,
  },
  valueContainer: {
    padding: 0,
    paddingLeft: getThemes(theme).space[2],
    paddingTop: 0,
    paddingBottom: 0,
  },
});

const md = (theme: ThemesKeys) => ({
  field: {
    borderRadius: getThemes(theme).radii.md,
  },
  menu: {
    borderRadius: getThemes(theme).space[3],
  },
  control: {
    minHeight: "40px",
  },
  indicatorsContainer: {
    marginRight: 5,
  },
  multiValueLabel: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  multiValue: {
    paddingTop: 0,
    paddingBottom: 0,
    height: getThemes(theme).space[8],
  },
});

const lg = (theme: ThemesKeys) => ({
  field: {
    borderRadius: getThemes(theme).radii.lg,
  },
  menu: {
    borderRadius: getThemes(theme).space[4],
  },
  control: {
    minHeight: "48px",
  },
  indicatorsContainer: {
    marginRight: 5,
  },
  multiValueLabel: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  multiValue: {
    paddingTop: 0,
    paddingBottom: 0,
    height: getThemes(theme).space[10],
  },
});

const sizes = (theme: ThemesKeys) => ({
  sm: sm(theme),
  md: md(theme),
  lg: lg(theme),
});

const outline = (theme: ThemesKeys) => ({
  bg: "transparent",
  color: getThemes(theme).colors.palette.primary[0],
  borderWidth: 1,
  borderColor: getThemes(theme).colors.palette.secondary[50],
});

const InputSelect = (theme: ThemesKeys) => {
  return helpers.defineMultiStyleConfig({
    baseStyle: baseStyle(theme),
    sizes: sizes(theme),
    // 3. We can add a new visual variant
    variants: {
      // @ts-expect-error FIXME!!!
      outline: outline(theme),
    },
  });
};
export default InputSelect;
